import React from "react";
import { useSelector } from "react-redux";
import { MenuItem } from "@mui/material";
import { ActionType } from "src/constants/table";
import { UserRoles } from "src/constants/user";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { ChevronDown, ChevronUp, EyeOpenedMenuIcon, PenMenuIcon, TrashBinMenuIcon } from "src/Icons";
import { FolderArrowIcon } from "src/Icons/FolderArrowIcon";
import { PublishIcon } from "src/Icons/PublishIcon";
import { ReactivateIcon } from "src/Icons/ReactivateIcon";
import { ServiceIcon } from "src/Icons/ServiceIcon";
import { activateAdmin, getAdmins, removeAdmin } from "src/store/reducers/adminsSlice";
import { getBookings , removeBookings } from "src/store/reducers/bookingsSlice";
import { getCategoryTemplates } from "src/store/reducers/categoryTemplatesSlice";
import {
  setAdminDialogParams,
  setBookingsDialogParams,
  setEmployeesDialogParams,
  setServicesDialogParams,
  setStudioDialogParams,
  showAdminDialog,
  showBookingsDialog,
  showEmployeesDialog,
  showServicesDialog,
  showStudioDialog
} from "src/store/reducers/dialogSlice";
import { openDrawer } from "src/store/reducers/drawerSlice";
import { getEmployees, getEmployeeStatus, removeEmployees } from "src/store/reducers/employeesSlice";
import { closeModal, showModal } from "src/store/reducers/modalSlice";
import { setSelectedStudio } from 'src/store/reducers/selectedStudioSlice';
import { removeServices } from 'src/store/reducers/servicesSlice';
import { removeServiceTemplate } from "src/store/reducers/serviceTemplatesSlice";
import { getStudios, publishStudio, removeStudios } from "src/store/reducers/studiosSlice";
import { setSelectedRow } from "src/store/reducers/tableSlice";
import { ActionButton, ActionMenu } from "src/styledComponents";
import { elementStyle } from "src/utils/helpers";

import { CustomActionsCellProps, CustomActionsCellTypes } from "./CustomActionsCell.types";

export function CustomActionsCell ({ params, type }: CustomActionsCellProps) {
  const state = useAppSelector((state) => state);
  const { user } = useSelector((state: any) => state.auth);
  const { categories } = useAppSelector((state: any) => state.categoryTemplates);
  const { studio: selectedStudio } = useAppSelector((state: any) => state.selectedStudio);

  //@ts-ignore
  const role = UserRoles[user?.role];

  const getCustomActionsCellLoading = (state: any, type: CustomActionsCellTypes) => {
    switch (type) {
      case CustomActionsCellTypes.ADMINS:
        return state.admins.isRemoveRequestLoading;

      case CustomActionsCellTypes.EMPLOYEES:
        return state.employees.isRemoveRequestLoading;

      case CustomActionsCellTypes.SERVICES:
        return state.services.isRemoveRequestLoading;

      case CustomActionsCellTypes.STUDIOS:
        return state.studios.isRemoveRequestLoading;

      case CustomActionsCellTypes.BOOKINGS:
        return state.bookings.isRemoveRequestLoading;

      default:
        return false;
    }
  }

  const loading = getCustomActionsCellLoading(state, type);

  const openDialogFromActionCell = () => {
    const dialogParams = {
      step: 0,
      isEdit: true,
      data: params.row,
    }
    switch (type) {
      case CustomActionsCellTypes.ADMINS:
        return {
          initialDialogDataSetter: setAdminDialogParams({...dialogParams, maxAvailableStep: 1}),
          dialogInitiator: showAdminDialog()
        };
      case CustomActionsCellTypes.EMPLOYEES :
        return {
          initialDialogDataSetter: setEmployeesDialogParams({...dialogParams, maxAvailableStep: 2}),
          dialogInitiator: showEmployeesDialog()
        };

      case CustomActionsCellTypes.SERVICES:
        return {
          initialDialogDataSetter: setServicesDialogParams({...dialogParams, maxAvailableStep: 0}),
          dialogInitiator: showServicesDialog()
        };

      case CustomActionsCellTypes.STUDIOS:
        return {
          initialDialogDataSetter: setStudioDialogParams({...dialogParams, maxAvailableStep: 3}),
          dialogInitiator: showStudioDialog()
        };

      case CustomActionsCellTypes.BOOKINGS:
        return {
          initialDialogDataSetter: setBookingsDialogParams({...dialogParams, maxAvailableStep: 1}),
          dialogInitiator: showBookingsDialog()
        };
    }
  }

  const getCustomActionsCellRemoveFunc = () => {
    let conditionalProps: any;
    let conditionalAction: any;
    let conditionalSuccessAction: any;

    switch (type) {
      case CustomActionsCellTypes.ADMINS:
        conditionalProps = {id: params.id};
        conditionalAction = removeAdmin;
        conditionalSuccessAction = getAdmins;
        break;

      case CustomActionsCellTypes.EMPLOYEES:
        conditionalProps = {
          employeeId: params.row.id,
          employeeStudios: params.row.studios.map((studio: any) => studio.id),
        };
        conditionalAction = removeEmployees;
        conditionalSuccessAction = getEmployees;
        break;

      case CustomActionsCellTypes.SERVICES:
        conditionalProps = selectedStudio ? {
          studioId: selectedStudio.id,
          serviceId: params.id,
        } : {
          serviceId: params.id,
        };
        conditionalAction = selectedStudio ? removeServices : removeServiceTemplate;
        conditionalSuccessAction = selectedStudio ? getStudios : getCategoryTemplates;
        break;

      case CustomActionsCellTypes.STUDIOS:
        conditionalProps = {currentId: params.id};
        conditionalAction = removeStudios;
        conditionalSuccessAction = getStudios;
        break;

      case CustomActionsCellTypes.BOOKINGS:
        conditionalProps = {bookingId: params.id};
        conditionalAction = removeBookings;
        conditionalSuccessAction = getBookings;
        break;
    }

    return conditionalAction(
      {
        ...conditionalProps,
        onSuccess: () => {
          dispatch(
            showModal({
              modalType: "SUCCESS",
              successText: `${type.slice(0, -1)} is successfully deleted`,
              okButtonTitle: "Ok, I got it",
              onButtonClick: () => {
                if (conditionalSuccessAction) {
                  dispatch(conditionalSuccessAction());
                }
                dispatch(closeModal());
              },
            })
          );
        },
        onFailure: (error: any) => {
          dispatch(
            showModal({
              modalType: "DELETE",
              title: `${error?.response?.data?.detail || error.message}`,
              okButtonTitle: "Ok, I got it",
              isButtonCancel: true,
              onButtonClick: () => {
                dispatch(closeModal());
              },
            })
          );
        },
      }
    )
  }

  const getCustomActionsCellText = () => {
    switch (type) {
      case CustomActionsCellTypes.ADMINS:
        return {
          title: 'Deleting an administrator from the CRM system will remove their access and privileges',
          subTitle: 'This admin is assigned to the following studios:',
          okButtonTitle: 'Yes, delete admin',
          description: params?.row?.studios?.length === 0
            ? 'Without studio'
            : `${params?.row?.studios
              ?.map((el: any) => el.title)
              .join('; ')}.`,
        }

      case  CustomActionsCellTypes.EMPLOYEES:
        return {
          title:
          "Are you sure you want to delete this employee? This action is irreversible and will permanently remove the employee's profile and associated data from the system",
          subTitle:
          "Please, confirm if you want to proceed with this action",
          okButtonTitle: "Yes, delete employee",
        }

      case CustomActionsCellTypes.SERVICES:
        return {
          title:
          "Are you sure you want to delete this service? This action is irreversible and will permanently remove the service's profile and associated data from the system",
          subTitle:
          "Please, confirm if you want to proceed with this action",
          okButtonTitle: "Yes, delete service",
        };

      case CustomActionsCellTypes.STUDIOS:
        return {
          title: ` Please , confirm your decision with deletion of the ${params?.row?.title}`,
          subTitle:
            'Deleting this studio will result in the following consequences:',
          description:
            'Staff members assigned exclusively to this studio will be unassigned and need to be reassigned to other studios. \n Any customized settings and preferences specific to this studio will be lost.',
        };

      case CustomActionsCellTypes.BOOKINGS:
        return {
          title: "If you are trying to cancel a booking, check whether the client has been informed about this action. It will not be possible to reverse this action, please confirm it.",
          checkTitle: "Client is informed, everything is ok!",
          okButtonTitle: "Yes, delete booking",
        }
    }
  }

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const checkIfReadyToPublish = (row: any) => {
    const isAdminsFulfilled = row?.admins.length > 0
    const isMastersFulfilled = row?.masters.length > 0
    const isCategoriesFulfilled = row?.categories.length > 0
    const isServicesFulfilled = row?.services.length > 0
    const isImagesFulfilled = row?.images.length > 0
    const isScheduleFulfilled = row?.schedule?.workingTimes?.length > 0
    return ( row.isDraft &&
      isAdminsFulfilled &&
      isMastersFulfilled &&
      isCategoriesFulfilled &&
      isServicesFulfilled &&
      isImagesFulfilled &&
      isScheduleFulfilled
    )
  }

  const dispatch = useAppDispatch();
  const handleAction = (action: ActionType) => {
    return () => {
      switch (action) {
        case ActionType.activate:
          dispatch(setSelectedRow(params.row));
          dispatch(activateAdmin({
            id: params.row.id,
            onSuccess: () => {
              dispatch(
                showModal({
                  modalType: "SUCCESS",
                  successText: "Admin has been activated",
                  okButtonTitle: "Ok, I got it",
                  onButtonClick: () => {
                    dispatch(getAdmins());
                    dispatch(closeModal());
                  }
                }))
            },
            onFailure: (error: any) => {dispatch(
              showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, I got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(getAdmins());
                  dispatch(closeModal());
                },
              })
            )}
          }));
          console.log('should Activate');
          break;
        case ActionType.publish:
          dispatch(setSelectedRow(params.row));
          dispatch(publishStudio({
            studioId: params.row.id,
            onSuccess: () => dispatch(getStudios()),
            onFailure: (error: any) => {console.log(error?.response?.data?.detail
              || error?.message
              || "PUBLISH ERROR: Not enough data to publish")}
          }));
          console.log('should Publish');
          break;
        case ActionType.services:
          dispatch(setSelectedStudio(params.row));
          console.log('should open services');
          break;
        case ActionType.move:
          dispatch(setSelectedRow(params.row));
          if (selectedStudio) {
            dispatch(
              showModal({
                modalType: 'MOVE',
                categories: selectedStudio.categories,
                data: params.row,
                onClose: () => {
                  dispatch(getStudios());
                  dispatch(closeModal());
                },
              })
            );
          } else {
            dispatch(
              showModal({
                modalType: 'MOVE',
                categories,
                data: params.row,
                onClose: () => {
                  dispatch(getCategoryTemplates());
                  dispatch(closeModal());
                },
              })
            );
          }
          console.log('should Move');
          break;
        case ActionType.view:
          dispatch(setSelectedRow(params.row));
          dispatch(openDrawer());
          console.log('should View');
          break;
        case ActionType.edit:
          dispatch(openDialogFromActionCell().initialDialogDataSetter);
          dispatch(openDialogFromActionCell().dialogInitiator);
          break;
        case ActionType.delete:
          if (type === CustomActionsCellTypes.EMPLOYEES) {
            dispatch(getEmployeeStatus({
              employeeId: params.row.id,
              onSuccess: (response: boolean) => {
                dispatch(
                  response ? showModal({
                    modalType: 'ATTENTION',
                    headerTitle: 'Attention!',
                    subTitle:`Deleting is impossible because this employee
                          has active client bookings scheduled. Please,
                          communicate with clients and transfer active
                          bookings before deleting`,
                    okButtonTitle: "Ok, got it",
                    isButtonCancel: true,
                    onButtonClick: () => {
                      dispatch(closeModal());
                    },
                  }) : showModal({
                    modalType: 'DELETE',
                    headerTitle: 'Are you sure?',
                    title: getCustomActionsCellText()?.title,
                    subTitle: getCustomActionsCellText()?.subTitle,
                    description: getCustomActionsCellText()?.description,
                    checkTitle: getCustomActionsCellText()?.checkTitle,
                    isLoading: loading,
                    okButtonTitle: 'Yes, delete',
                    onButtonClick: () => {
                      dispatch(closeModal());
                      //@ts-ignore
                      dispatch(getCustomActionsCellRemoveFunc());
                    },
                    onButtonCancelClick: () => {
                      dispatch(closeModal());
                    },
                  })
                )
              },
              onFailure: (error: any) => dispatch(showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              }))
            }))
          } else {
            dispatch(
              showModal({
                modalType: 'DELETE',
                headerTitle: 'Are you sure?',
                title: getCustomActionsCellText()?.title,
                subTitle: getCustomActionsCellText()?.subTitle,
                description: getCustomActionsCellText()?.description,
                checkTitle: getCustomActionsCellText()?.checkTitle,
                isLoading: loading,
                okButtonTitle: 'Yes, delete',
                onButtonClick: () => {
                  dispatch(closeModal());
                  //@ts-ignore
                  dispatch(getCustomActionsCellRemoveFunc());
                },
                onButtonCancelClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          }
          console.log('should Delete');
          break;
        default:
          throw new Error('Not proper ActionType');
      }
      setAnchorEl(null);
    };
  };


  return (
    <div>
      <ActionButton
        id="action-button"
        variant="outlined"
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        endIcon={open ? <ChevronUp /> : <ChevronDown />}
        disableRipple
      >
        Actions
      </ActionButton>
      <ActionMenu
        id="action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'action-button',
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {(CustomActionsCellTypes.ADMINS && params.row.status === "deleted") ?
          null : type === CustomActionsCellTypes.SERVICES ?
            <MenuItem
              onClick={
                handleAction(ActionType.move)
              }
              disableRipple
            >
              <FolderArrowIcon />
              Move
            </MenuItem>
            :
            <MenuItem
              onClick={
                handleAction(ActionType.view)
              }
              disableRipple
            >
              <EyeOpenedMenuIcon />
              View
            </MenuItem>
        }

        {!(CustomActionsCellTypes.ADMINS && params.row.status === "deleted") ?
          <MenuItem
            sx={type === CustomActionsCellTypes.BOOKINGS ? {
              display: elementStyle({
                role,
                roleType: ["SUPER_ADMIN", "MASTER"],
                firstOption: "none",
              // lastOption: "inline-flex",
              }),
            } : {}}
            onClick={handleAction(ActionType.edit)}
            disableRipple
          >
            <PenMenuIcon />
          Edit
          </MenuItem> : null
        }

        {type === CustomActionsCellTypes.STUDIOS && checkIfReadyToPublish(params.row) &&
          <MenuItem
            onClick={
              handleAction(ActionType.publish)
            }
            disableRipple
          >
            <PublishIcon />
          Publish
          </MenuItem>
        }
        {type === CustomActionsCellTypes.STUDIOS &&
          <MenuItem
            onClick={
              handleAction(ActionType.services)
            }
            disableRipple
          >
            <ServiceIcon />
          Services
          </MenuItem>
        }
        {CustomActionsCellTypes.ADMINS && params.row.status === "deleted" ?
          <MenuItem
            onClick={
              handleAction(ActionType.activate)
            }
            disableRipple
          >
            <ReactivateIcon />
            Activate
          </MenuItem> :
          <MenuItem
            sx={type === CustomActionsCellTypes.BOOKINGS ? {
              display: elementStyle({
                role,
                roleType: ["SUPER_ADMIN", "MASTER"],
                firstOption: "none",
              // lastOption: "inline-flex",
              }),
            } : {}}
            onClick={handleAction(ActionType.delete)}
            disableRipple>
            <TrashBinMenuIcon />
          Delete
          </MenuItem>
        }
      </ActionMenu>
    </div>
  );
}
